import { z } from 'zod';
import { ErrorCodes, type ErrorCode } from '../codegen/error_codes.js';
import type { DecoderFunction } from '../decoder.js';
import { isPojoObject, nameSchema, timeStampSchema } from '../decoders.js';
import { Timestamp } from '../time/index.js';
import { truncate } from '../truncate.js';

type Timestamps = Map<string, Timestamp>;

const editorSchema = z.union([
  z.object({
    lastEdit: z.enum(['InitCase', 'CurrentUser', 'External', 'Unknown', 'EmptyEdit']),
  }),
  z.object({
    lastEdit: z.literal('User'),
    data: nameSchema,
  }),
]);
export type Editor = z.infer<typeof editorSchema>;

const versionsSchema = z
  .record(z.string(), timeStampSchema)
  .transform((record): Timestamps => new Map(Object.entries(record)));

export type HttpSuccess<T> = {
  status: 'Success';
  data: T;
  editor: Editor;
  lastEdit: Timestamp;
  versions: Timestamps;
};

interface HttpFailureInterface {
  status: 'Failure';
  code: number;
  message: string;
}

const httpFailureInterfaceSchema = z.object({
  code: z.number(),
  message: z.string(),
});

/** The registry's generic code per HTTP status, for a failure whose body carries no code of its
 * own. Listed only where the generic copy suits an unattributed failure; the rest report
 * `GENERAL_UNSPECIFIED`. */
const genericCodeByStatus: Partial<Record<number, ErrorCode>> = {
  400: ErrorCodes.GENERAL_BAD_REQUEST,
  403: ErrorCodes.GENERAL_FORBIDDEN,
  404: ErrorCodes.GENERAL_NOT_FOUND,
  429: ErrorCodes.GENERAL_TOO_MANY_REQUESTS,
  500: ErrorCodes.GENERAL_UNEXPECTED,
  502: ErrorCodes.GENERAL_BAD_GATEWAY,
  504: ErrorCodes.GENERAL_TIMEOUT,
};

/** Caps the body excerpt: an unattributed failure answers with a whole error page, and the
 * message reaches the log as one line. */
const BODY_EXCERPT_LENGTH = 200;

function genericCodeForStatus(status: number): ErrorCode {
  return genericCodeByStatus[status] ?? ErrorCodes.GENERAL_UNSPECIFIED;
}

function parseJson(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
}

/** Names a failure whose body holds no `code`/`message`. Keeps the body, the only account of
 * what went wrong, and the status, since `statusText` is always empty over HTTP/2. */
function unreadableBodyMessage(res: Response, body: string): string {
  const excerpt = truncate(body.replace(/\s+/g, ' ').trim(), BODY_EXCERPT_LENGTH, true);

  return [String(res.status), res.statusText, excerpt].filter((part) => part !== '').join(' ');
}

export class HttpFailure implements HttpFailureInterface {
  code: number;
  message: string;
  status: 'Failure';

  private constructor(code: number, message: string) {
    this.code = code;
    this.message = message;
    this.status = 'Failure';
  }

  static async fromResponse(res: Response): Promise<HttpFailure> {
    if (res.ok) throw 'Can not create HttpFailure when res is ok';

    const body = await res.text().catch(() => '');
    const parsed = httpFailureInterfaceSchema.safeParse(parseJson(body));

    if (parsed.success) {
      return new HttpFailure(parsed.data.code, parsed.data.message);
    }

    return new HttpFailure(genericCodeForStatus(res.status), unreadableBodyMessage(res, body));
  }

  static fromObject(obj: HttpFailureInterface): HttpFailure {
    const decoded = httpFailureInterfaceSchema.parse(obj);
    return new HttpFailure(decoded.code, decoded.message);
  }

  toJSON() {
    return `${this.code} ${this.message} ${this.status}`;
  }

  stringify() {
    return this.toJSON();
  }
}

export type HttpResponse<T> = HttpSuccess<T> | HttpFailure;
export const responseDecoder =
  <T>(decoder: DecoderFunction<T>): DecoderFunction<HttpResponse<T>> =>
  (value: unknown): HttpResponse<T> => {
    if (!isPojoObject(value)) {
      throw `JSON \`${value}\` is not an object and can therefore not be parsed into a Response`;
    }

    if (value.status === 'Success') {
      return {
        status: 'Success',
        data: decoder(value.data),
        editor: editorSchema.parse(value.editor),
        lastEdit: timeStampSchema.parse(value.lastEdit),
        versions: versionsSchema.parse(value.versions),
      };
    } else {
      return HttpFailure.fromObject({
        status: 'Failure',
        code: z.number().parse(value.code),
        message: z.string().parse(value.message),
      });
    }
  };
